// VO2max from a steady ride with power and heart rate (#194).
//
// The approach Garmin/Firstbeat-style estimates rest on, done in the open:
//   1. Oxygen cost of the power you held, from the ACSM leg-cycling equation:
//        VO2 (ml/kg/min) = 10.8 × watts / kg + 7
//      (1.8 ml per kg·m of work, plus 3.5 resting and 3.5 for unloaded
//      pedalling; ACSM's Guidelines for Exercise Testing and Prescription.)
//   2. Heart-rate reserve tracks VO2 reserve closely (Swain & Leutholtz 1997),
//      so the fraction of your reserve you were working at says how far that
//      VO2 is from your max:
//        VO2max = 3.5 + (VO2 − 3.5) / %HRR
//
// It needs a genuinely steady effort: a window of WINDOW_S seconds on a bike
// with power and heart rate nearly all the way through, power steady, heart
// rate settled (little drift), and working at 50 to 90% of your reserve.
// Every window in the ride that passes counts: the estimate is their average,
// so a longer steady ride gives a steadier number. Expect about ±10–15% from
// one ride, like any submaximal estimate; currentVo2max() smooths across rides.

export const WINDOW_S = 600; // 10 minutes
const SETTLE_S = 180; // heart rate is read over the window's last 3 minutes
const MIN_COVERAGE = 0.9; // share of seconds with both power and HR
const MAX_POWER_CV = 0.12; // coefficient of variation of power
const MAX_HR_DRIFT = 2; // bpm per minute over the settled part (steady-state drift is ~1)
const HRR_RANGE = [0.5, 0.9];
const STEP_S = 60; // windows start every minute
export const RECENT_DAYS = 56; // currentVo2max looks back 8 weeks
export const RECENT_COUNT = 5; // and uses up to the last 5 rides

const mean = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
const cv = (xs) => {
  const m = mean(xs);
  if (!m) return Infinity;
  const v = mean(xs.map((x) => (x - m) ** 2));
  return Math.sqrt(v) / m;
};
// Least-squares slope, per sample.
const slope = (ys) => {
  const n = ys.length;
  const xm = (n - 1) / 2;
  const ym = mean(ys);
  let num = 0;
  let den = 0;
  ys.forEach((y, i) => {
    num += (i - xm) * (y - ym);
    den += (i - xm) ** 2;
  });
  return den ? num / den : 0;
};

/** ACSM leg-cycling oxygen cost, ml/kg/min. */
export const cyclingVo2 = (watts, kg) => (10.8 * watts) / kg + 7;

/** VO2max from the oxygen cost of a steady effort and the %HRR it took. */
export const vo2maxFromSubmax = (vo2, pctHrr) => 3.5 + (vo2 - 3.5) / pctHrr;

/**
 * Every steady 10-minute window in `rows` (1 Hz, null where they don't
 * count): power steady, heart rate settled, 50 to 90% of your reserve.
 * Returns the windows' mean watts, settled heart rate and %HRR, and how many
 * seconds of the ride they cover.
 */
function steadyWindows(rows, basis, reserve) {
  const found = [];
  let coveredTo = 0;
  let covered = 0;
  for (let start = 0; start + WINDOW_S <= rows.length; start += STEP_S) {
    const win = rows.slice(start, start + WINDOW_S);
    const both = win.filter((r) => r && r.w > 0 && r.hr > 0);
    if (both.length < WINDOW_S * MIN_COVERAGE) continue;
    const watts = both.map((r) => r.w);
    if (cv(watts) > MAX_POWER_CV) continue;
    const settled = win.slice(WINDOW_S - SETTLE_S).filter((r) => r && r.hr > 0).map((r) => r.hr);
    if (settled.length < SETTLE_S * MIN_COVERAGE) continue;
    if (Math.abs(slope(settled) * 60) > MAX_HR_DRIFT) continue;
    const hr = mean(settled);
    const pct = (hr - basis.resting) / reserve;
    if (pct < HRR_RANGE[0] || pct > HRR_RANGE[1]) continue;
    found.push({ w: mean(watts), hr, pct });
    // Seconds of the ride inside at least one qualifying window.
    covered += start + WINDOW_S - Math.max(start, coveredTo);
    coveredTo = start + WINDOW_S;
  }
  return { found, covered };
}

const reserveOf = (basis) => (basis?.max && basis?.resting ? basis.max - basis.resting : 0);

/**
 * The estimate from a session's 1 Hz telemetry, or null when no window
 * qualifies. `basis` is heartRateBasis(profile): { max, resting, weightKg }.
 * Averages every qualifying 10-minute window; `minutes` is how much of the
 * ride they cover. `max_estimated` notes a max heart rate taken from age,
 * the largest source of error.
 *
 * @returns {null | { value: number, watts: number, hr: number, pct_hrr: number, minutes: number, windows: number, max_estimated?: true }}
 */
export function estimateVo2max(samples, basis) {
  const kg = Number(basis?.weightKg);
  const reserve = reserveOf(basis);
  if (!Array.isArray(samples) || !(kg > 0) || !(reserve > 20)) return null;
  // Bike seconds only; rowing has a different oxygen cost per watt.
  // Air bikes are out too: arms add to the cost, and their watts are estimated.
  const rows = samples.map((r) => (r && r.dev === "indoor_bike" && !r.air ? r : null));
  const { found, covered } = steadyWindows(rows, basis, reserve);
  if (found.length === 0) return null;
  const avg = (fn) => mean(found.map(fn));
  return {
    value: Math.round(avg((f) => vo2maxFromSubmax(cyclingVo2(f.w, kg), f.pct)) * 10) / 10,
    watts: Math.round(avg((f) => f.w)),
    hr: Math.round(avg((f) => f.hr)),
    pct_hrr: Math.round(avg((f) => f.pct) * 100) / 100,
    minutes: Math.round(covered / 60),
    windows: found.length,
    ...(basis.maxEstimated ? { max_estimated: true } : {}),
  };
}

/**
 * An air bike's steady effort, kept so it can be calibrated against your
 * rowing VO2max (utils/airCalibration): watts and %HRR averaged over every
 * steady window, as for a bike, but no VO2max of its own.
 *
 * @returns {null | { watts: number, hr: number, pct_hrr: number, minutes: number, windows: number }}
 */
export function airBikeSteady(samples, basis) {
  const reserve = reserveOf(basis);
  if (!Array.isArray(samples) || !(reserve > 20)) return null;
  const rows = samples.map((r) => (r && r.dev === "indoor_bike" && r.air ? r : null));
  const { found, covered } = steadyWindows(rows, basis, reserve);
  if (found.length === 0) return null;
  const avg = (fn) => mean(found.map(fn));
  return {
    watts: Math.round(avg((f) => f.w)),
    hr: Math.round(avg((f) => f.hr)),
    pct_hrr: Math.round(avg((f) => f.pct) * 1000) / 1000,
    minutes: Math.round(covered / 60),
    windows: found.length,
  };
}

const DAY_MS = 86400000;

/**
 * Your VO2max now, from the ride estimates (oldest first, as the server
 * returns them): the median of up to the last RECENT_COUNT within RECENT_DAYS
 * of the newest, so one odd ride doesn't swing it. Null with none.
 *
 * @returns {null | { value: number, count: number }}
 */
export function currentVo2max(rows) {
  const valid = (rows || []).filter((r) => r?.vo2max?.value > 0 && r.started_at);
  if (valid.length === 0) return null;
  const newest = new Date(valid[valid.length - 1].started_at).getTime();
  const recent = valid
    .filter((r) => newest - new Date(r.started_at).getTime() <= RECENT_DAYS * DAY_MS)
    .slice(-RECENT_COUNT)
    .map((r) => r.vo2max.value)
    .sort((a, b) => a - b);
  const mid = Math.floor(recent.length / 2);
  const median = recent.length % 2 ? recent[mid] : (recent[mid - 1] + recent[mid]) / 2;
  return { value: Math.round(median * 10) / 10, count: recent.length };
}
